import React, { useEffect, useMemo, useState } from 'react';

export function normalizeStatus(value) {
  const status = value && typeof value === 'object' ? value : {};
  const rejectedCount = Math.max(0, Number(status.rejectedCount || status.rejected || 0));
  const pendingCount = Math.max(0, Number(status.pendingCount || status.pending || 0));
  const online = status.online !== false && status.connectivity !== 'offline';
  const phase = status.operationsBlocked === true
    ? 'blocked'
    : String(status.phase || status.status || status.connectivity || '').trim().toLowerCase();
  return {
    ...status,
    rejectedCount,
    pendingCount,
    online,
    phase,
    cursor: String(status.acknowledgedCursor || status.cursor || '0')
  };
}

export function noticeFor(status) {
  if (!status) return null;
  if (status.rejectedCount > 0 || ['rejected', 'conflict', 'blocked'].includes(status.phase)) {
    return {
      tone: 'danger',
      icon: 'fa-exclamation-triangle',
      title: 'Sincronizacao bloqueada',
      text: status.message || 'Existe uma operacao rejeitada ou em conflito. Novas vendas deste estoque ficam bloqueadas ate a conciliacao.'
    };
  }
  if (!status.online) {
    return {
      tone: 'warning',
      icon: 'fa-wifi',
      title: 'Operando localmente',
      text: status.message || 'A nuvem está indisponível, mas os dados continuam protegidos neste computador e serão sincronizados automaticamente.'
    };
  }
  if (['offline', 'error'].includes(status.phase)) {
    return {
      tone: 'warning',
      icon: 'fa-sync-alt',
      title: 'Sincronização interrompida',
      text: status.message || 'A conexão está ativa, mas a sincronização central precisa ser retomada.'
    };
  }
  if (status.backgroundWork === true) return null;
  // O polling de fundo entra em `syncing` a cada ciclo mesmo sem trabalho
  // pendente. Exibir um banner nesse estado causa um falso alerta intermitente.
  if (status.pendingCount > 0 || ['pending', 'inflight', 'reconciling'].includes(status.phase)) {
    return {
      tone: 'info',
      icon: 'fa-sync-alt',
      title: 'Confirmando dados',
      text: status.message || `${status.pendingCount || 1} operacao(oes) aguardando confirmacao central.`
    };
  }
  return null;
}

function SyncStatusNotice({ user }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user || !['central', 'offline_signed'].includes(user.session_mode)) {
      setStatus(null);
      return undefined;
    }
    const api = window.electron?.sync;
    if (!api || typeof api.getStatus !== 'function') return undefined;
    let active = true;
    Promise.resolve(api.getStatus())
      .then((next) => { if (active) setStatus(normalizeStatus(next)); })
      .catch(() => { if (active) setStatus(normalizeStatus({ online: false })); });
    const unsubscribe = typeof api.onStatus === 'function'
      ? api.onStatus((next) => { if (active) setStatus(normalizeStatus(next)); })
      : null;
    return () => {
      active = false;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [user]);

  const notice = useMemo(() => noticeFor(status), [status]);
  if (!notice) return null;

  return (
    <div className={`sync-status-notice sync-status-notice--${notice.tone}`} role="status" aria-live="polite">
      <i className={`fas ${notice.icon}`} aria-hidden="true" />
      <div>
        <strong>{notice.title}</strong>
        <span>{notice.text}</span>
      </div>
      {status?.cursor !== '0' && <small>Cursor confirmado: {status.cursor}</small>}
    </div>
  );
}

export default SyncStatusNotice;
